<template>
	<!-- Datalist for room name: browser shows only these values as suggestions (like old IPA). -->
	<datalist id="room_names_datalist">
		<option
			v-for="opt in roomNameOptionsWithHistory"
			:key="opt"
			:value="opt" />
	</datalist>
	<v-card class="mt-2">
		<v-toolbar>
			<v-toolbar-title
				:class="
					$vuetify.display.mdAndUp ? 'text-h6' : 'text-subtitle-1'
				"
				>Detalii Camere (IPA)</v-toolbar-title
			>
			<v-spacer></v-spacer>

			<v-checkbox
				:model-value="localShowApartments"
				hide-details
				density="compact"
				class="ml-4 mr-4 mt-1 custom-checkbox"
				color="black"
				:label="$vuetify.display.mdAndUp ? 'APARTAMENTE' : ''"
				@update:modelValue="onToggleApartments">
			</v-checkbox>
			<v-snackbar
				v-model="confirmSnackbar"
				timeout="-1"
				color="error"
				elevation="3"
				location="center center">
				<div class="d-flex align-center ga-3">
					<v-icon color="white" size="26">mdi-alert-outline</v-icon>
					<div>
						<strong>Atenție!</strong><br />
						Dezactivarea vizualizării apartamentelor va duce la
						<b>ștergerea tuturor selecțiilor de apartamente</b>
						din camere.
					</div>
				</div>
				<template #actions>
					<v-spacer />
					<v-btn
						prepend-icon="mdi-cancel"
						variant="text"
						color="white"
						class="text-none"
						@click="cancelDisableApartments">
						Anulează
					</v-btn>
					<v-btn
						prepend-icon="mdi-check-circle"
						variant="elevated"
						color="success"
						class="text-none"
						@click="confirmDisableApartments">
						Confirmă
					</v-btn>
				</template>
			</v-snackbar>
			<!-- Grouping: only floor or ungrouped -->
			<v-select
				v-if="$vuetify.display.mdAndUp"
				v-model="groupOption"
				:items="groupSelectItems"
				item-title="title"
				item-value="value"
				density="compact"
				variant="outlined"
				hide-details
				label="Grupare"
				@update:modelValue="changeGroupingProperty"
				style="max-width: 200px; min-width: 130px" />

			<v-menu v-else>
				<template #activator="{ props }">
					<v-btn
						icon
						v-bind="props"
						variant="text"
						color="primary"
						title="Selectează grupare">
						<v-icon>mdi-view-grid-plus-outline</v-icon>
					</v-btn>
				</template>

				<v-list density="compact" style="min-width: 160px">
					<v-list-subheader>Grupare</v-list-subheader>

					<v-list-item
						v-for="opt in groupSelectItems"
						:key="opt.value"
						@click="
							groupOption = opt.value;
							changeGroupingProperty(opt.value);
						"
						:active="groupOption === opt.value"
						color="primary">
						<v-list-item-title>{{ opt.title }}</v-list-item-title>

						<template #append>
							<v-icon
								v-if="groupOption === opt.value"
								color="primary"
								size="18"
								>mdi-check</v-icon
							>
						</template>
					</v-list-item>
				</v-list>
			</v-menu>

			<!-- Add Room Button -->
			<v-btn icon data-cy="add-room" @click="addRoom()">
				<v-icon>mdi-plus</v-icon>
				<v-tooltip activator="parent" location="top">
					Adaugă cameră
				</v-tooltip>
			</v-btn>

			<!-- Conditionally show "Salvează versiune nouă" vs "Salvează locație" -->
			<v-btn
				v-if="isNewVersionRequired"
				icon
				@click="setDescriptionDialogTrue()">
				<v-icon>mdi-content-save-plus-outline</v-icon>
				<v-tooltip activator="parent" location="top">
					Salvează versiune nouă
				</v-tooltip>
			</v-btn>
			<v-btn v-else icon @click="saveLocation()">
				<v-icon>mdi-content-save-edit-outline</v-icon>
				<v-tooltip activator="parent" location="top">
					Salvează locație
				</v-tooltip>
			</v-btn>

			<!-- Description dialog (for versioning) -->
			<v-dialog v-model="descriptionDialog" width="600px">
				<v-card>
					<v-card-title>Descriere modificare</v-card-title>
					<v-card-text>
						<v-textarea
							v-model="change_description"
							label="Descriere schimbare"></v-textarea>
					</v-card-text>
					<v-card-actions class="justify-content-between">
						<v-btn
							@click="setDescriptionDialogFalse"
							variant="elevated">
							Anulează
						</v-btn>
						<v-btn @click="saveLocation()" variant="elevated">
							Salvează
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Reset all rooms button (optional) -->
			<v-btn icon>
				<v-icon>mdi-restart-alert</v-icon>
				<v-tooltip activator="parent" location="top">
					Resetează camerele
				</v-tooltip>
				<v-dialog
					v-model="resetDialog"
					width="600px"
					activator="parent">
					<v-card>
						<v-toolbar>
							<v-toolbar-title
								>Resetează camerele</v-toolbar-title
							>
						</v-toolbar>
						<v-card-text>
							<p>
								Resetează camerele la valorile inițiale. Toate
								modificările manuale vor fi pierdute.
							</p>
							<p>Resetează camerele?</p>
						</v-card-text>
						<v-card-actions class="justify-content-between">
							<v-btn
								@click="resetDialog = false"
								variant="elevated">
								Anulează
							</v-btn>
							<v-btn
								@click="resetAllRooms()"
								variant="elevated"
								color="danger">
								Resetează
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</v-btn>
		</v-toolbar>

		<!-- Data Table -->
		<v-data-table
			:headers="vHeaders"
			:items="rooms"
			class="elevation-1"
			item-key="localId"
			items-per-page="-1"
			:group-by="groupBy"
			show-expand
			data-cy="rooms-table"
			:elevation="0">
			<!-- GROUP HEADER (Floor or Ungrouped) -->
			<template
				v-slot:group-header="{
					item,
					columns,
					toggleGroup,
					isGroupOpen,
				}">
				<tr>
					<td :colspan="columns.length">
						<v-btn
							size="small"
							variant="text"
							:ref="`group-${item.value}`"
							:data-open="isGroupOpen(item)"
							@click="toggleGroup(item)">
							<v-icon>{{
								isGroupOpen(item)
									? "mdi-chevron-up"
									: "mdi-chevron-down"
							}}</v-icon>
						</v-btn>
						{{ getGroupName(item.value) }} ({{ item.items.length }})
						<!--  Etaj -->
						<template
							v-if="
								groupBy[0]?.key === 'floor' &&
								totalsByFloor[item.value]
							">
							<v-chip
								class="ma-1"
								color="teal"
								text-color="white"
								variant="outlined"
								size="small">
								Suprafață:
								{{
									formatNumber(
										this,
										totalsByFloor[item.value].area,
										false
									)
								}}
								m²
							</v-chip>
							<v-chip
								class="ma-1"
								color="teal"
								text-color="white"
								variant="outlined"
								size="small">
								Țeavă:
								{{
									formatNumber(
										this,
										totalsByFloor[item.value].pipe,
										false
									)
								}}
								m
							</v-chip>
							<v-chip
								class="ma-1"
								color="teal"
								text-color="white"
								variant="outlined"
								size="small">
								Circuite:
								{{ totalsByFloor[item.value].circuits }}
							</v-chip>
							<!-- <v-chip
                                class="ma-1"
                                color="teal"
                                text-color="white"
                                variant="outlined"
                                size="small"
                            >
                                Lungime circuite:
                                {{
                                    totalsByFloor[
                                        item.value
                                    ].circuitLength.toFixed(2)
                                }}
                                m
                            </v-chip> -->
						</template>

						<!-- Apartament -->
						<template
							v-else-if="
								groupBy[0]?.key === 'apartament' &&
								totalsByApartment[item.value]
							">
							<v-chip
								class="ma-1"
								color="success"
								text-color="white"
								variant="outlined"
								size="small">
								Suprafață:
								{{
									formatNumber(
										this,
										totalsByApartment[item.value].area || 0,
										false
									)
								}}
								m²
							</v-chip>
							<v-chip
								class="ma-1"
								color="success"
								text-color="white"
								variant="outlined"
								size="small">
								Țeavă:
								{{
									formatNumber(
										this,
										totalsByApartment[item.value].pipe || 0,
										false
									)
								}}
								m
							</v-chip>
							<v-chip
								class="ma-1"
								color="success"
								text-color="white"
								variant="outlined"
								size="small">
								Circuite:
								{{
									totalsByApartment[item.value].circuits || 0
								}}
							</v-chip>
							<!-- <v-chip
                                class="ma-1"
                                color="success"
                                text-color="white"
                                variant="outlined"
                                size="small"
                            >
                                Lungime circuite:
                                {{
                                    totalsByApartment[
                                        item.value
                                    ].circuitLength?.toFixed(2) || 0
                                }}
                                m
                            </v-chip> -->
						</template>
					</td>
				</tr>
			</template>
			<template v-slot:[`item.isExcludedFromAdjust`]="{ item }">
				<v-checkbox
					v-model="item.isExcludedFromAdjust"
					hide-details
					color="success"
					@update:modelValue="onToggleAdjust(item)">
					<v-tooltip activator="parent" location="top">
						Bifarea acestei casuțe va exclude camera din calcule de
						ajustare.
					</v-tooltip>
				</v-checkbox>
			</template>

			<!-- ROOM NAME: native input + datalist (like old IPA – browser shows list and remembers across sessions). -->
			<template v-slot:[`item.name`]="{ item }">
				<input
					:ref="'roomName' + item.localId"
					:value="item.name"
					list="room_names_datalist"
					name="room_name"
					autocomplete="on"
					type="text"
					class="room-name-input"
					data-cy="room-name"
					@input="item.name = ($event.target && $event.target.value) || ''"
					@focus="storeOldValue(item, 'name')"
					@blur="updateField(item, 'name', false)"
					@keyup.enter="updateField(item, 'name', false)"
				/>
			</template>

			<!-- FLOOR SELECT -->
			<template v-slot:[`item.floor`]="{ item }">
				<v-select
					v-model="item.floor"
					variant="underlined"
					data-cy="room-floor"
					hide-details
					density="compact"
					:items="floors"
					item-title="name"
					item-value="value"
					@update:modelValue="
						this.updateField(item, 'floor')
					"></v-select>
			</template>
			<!--APARTAMENTS-->
			<template
				v-if="localShowApartments"
				v-slot:[`item.apartament`]="{ item }">
				<ApartamentSelect
					v-model="item.apartament"
					@update:modelValue="
						val => {
							item.apartament = val || 0;
							updateField(item, 'apartament');

							if (!validateUniqueApartmentPerFloor()) {
								// dacă nu e valid, resetează selecția
								item.apartament = null;
								updateField(item, 'apartament');
							}
						}
					" />
			</template>

			<!-- AREA -->
			<template v-slot:[`item.area.sq_m`]="{ item }">
				<v-text-field
					:ref="'roomArea' + item.localId"
					type="number"
					v-model="item.area.sq_m"
					variant="underlined"
					data-cy="room-area"
					density="compact"
					class="text-right no-arrows"
					hide-details
					@focus="handleFocusWithOldValue($event, item, 'area.sq_m')"
					@keyup.enter="updateField(item, 'area', true)"
					@blur="updateField(item, 'area', true, true)"
					@keydown.tab.prevent="handleTab(item, 'area')"
					suffix="m²" />
			</template>
			<template v-slot:[`item.mounting_step`]="{ item }">
				<v-select
					v-model="item.mounting_step"
					variant="underlined"
					data-cy="room-mounting-step"
					hide-details
					density="compact"
					:items="mountingStepOptions"
					item-title="label"
					item-value="value"
					:disabled="isMountingStepDisabled(item)"
					@update:modelValue="
						updateField(item, 'mounting_step')
					"></v-select>
			</template>
			<!-- TOTAL ȚEAVĂ (Editable) -->
			<template v-slot:[`item.total_pipe`]="{ item }">
				<v-text-field
					type="number"
					v-model="item.total_pipe"
					variant="underlined"
					data-cy="room-total-pipe"
					density="compact"
					class="text-right no-arrows"
					:class="{ 'manual-red': item._manualTotalPipe }"
					hide-details
					@focus="storeOldValue(item, 'total_pipe')"
					@blur="updateField(item, 'total_pipe')"
					@keyup.enter="updateField(item, 'total_pipe')"
					suffix="m">
				</v-text-field>
			</template>

			<!-- NR. CIRCUITE (Editable) -->
			<template v-slot:[`item.circuit_no`]="{ item }">
				<v-text-field
					type="number"
					v-model="item.circuit_no"
					variant="underlined"
					data-cy="room-circuit-no"
					density="compact"
					class="text-right no-arrows"
					:class="{ 'manual-red': item._manualCircuitNo }"
					hide-details
					@focus="storeOldValue(item, 'circuit_no')"
					@blur="updateField(item, 'circuit_no')"
					@keyup.enter="
						updateField(item, 'circuit_no')
					"></v-text-field>
			</template>

			<!-- CIRCUIT LENGTH (Editable) -->
			<template v-slot:[`item.circuit_length`]="{ item }">
				<v-text-field
					v-model="item.circuit_length"
					variant="underlined"
					data-cy="room-circuit-length"
					density="compact"
					class="text-right no-arrows"
					:class="{ 'manual-red': item._manualCircuitLength }"
					hide-details
					suffix="m"
					@focus="storeOldValue(item, 'circuit_length')"
					@blur="updateField(item, 'circuit_length')"
					@keyup.enter="updateField(item, 'circuit_length')">
				</v-text-field>
			</template>

			<!-- ACTIONS MENU -->
			<template v-slot:[`item.actions`]="{ item }">
				<v-btn icon variant="plain">
					<v-icon color="primary">mdi-dots-vertical</v-icon>
					<v-menu activator="parent">
						<v-list>
							<v-list-item @click="deleteRoom(item.localId)">
								<v-list-item-title
									>Șterge cameră</v-list-item-title
								>
							</v-list-item>
							<v-list-item @click="resetOneRoom(item.localId)">
								<v-list-item-title
									>Resetează valori</v-list-item-title
								>
							</v-list-item>
						</v-list>
					</v-menu>
				</v-btn>
			</template>
			<template v-slot:bottom>
				<v-toolbar dense flat class="pa-2 custom-toolbar">
					<!--  Desktop view -->
					<template v-if="$vuetify.display.mdAndUp">
						<v-sheet class="w-100 pt-3" color="transparent">
							<v-row dense>
								<v-col cols="12">
									<v-chip
										class="ma-1 ml-3"
										color="primary"
										text-color="white"
										variant="outlined">
										Total Suprafață:
										{{
											formatNumber(this, totalArea, false)
										}}
										m²
									</v-chip>
									<v-chip
										class="ma-1"
										color="primary"
										text-color="white"
										variant="outlined">
										Total Țeavă:
										{{
											formatNumber(this, totalPipe, false)
										}}
										m
									</v-chip>
									<v-chip
										class="ma-1"
										color="primary"
										text-color="white"
										variant="outlined">
										Total Circuite: {{ totalCircuits }}
									</v-chip>
									<!-- <v-chip
                                        class="ma-1"
                                        color="primary"
                                        text-color="white"
                                        variant="outlined"
                                    >
                                        Total Lungime Circuite:
                                        {{ totalCircuitsLength.toFixed(2) }} m
                                    </v-chip> -->
								</v-col>
							</v-row>
						</v-sheet>
					</template>

					<!--  Tablet/Mobile view -->
					<template v-else>
						<v-menu>
							<template #activator="{ props }">
								<v-btn
									v-bind="props"
									icon
									color="primary"
									variant="text"
									:title="'Statistici generale'">
									<v-icon>mdi-chart-box-outline</v-icon>
								</v-btn>
							</template>

							<v-list class="py-0">
								<v-list-item>
									<v-list-item-title class="text-caption">
										<strong>Total Suprafață:</strong>
										{{
											formatNumber(this, totalArea, false)
										}}
										m²
									</v-list-item-title>
								</v-list-item>

								<v-list-item>
									<v-list-item-title class="text-caption">
										<strong>Total Țeavă:</strong>
										{{
											formatNumber(this, totalPipe, false)
										}}
										m
									</v-list-item-title>
								</v-list-item>

								<v-list-item>
									<v-list-item-title class="text-caption">
										<strong>Total Circuite:</strong>
										{{ totalCircuits }}
									</v-list-item-title>
								</v-list-item>

								<!-- <v-list-item>
                                    <v-list-item-title class="text-caption">
                                        <strong>Total Lungime Circuite:</strong>
                                        {{ totalCircuitsLength.toFixed(2) }}
                                    </v-list-item-title>
                                </v-list-item> -->
							</v-list>
						</v-menu>
						<!-- Etaje -->
						<v-menu v-if="Object.keys(totalsByFloor).length > 1">
							<template #activator="{ props }">
								<v-btn
									v-bind="props"
									icon
									color="teal"
									variant="text"
									:title="'Totaluri pe etaj'">
									<v-icon>mdi-floor-plan</v-icon>
								</v-btn>
							</template>

							<v-list
								class="py-2"
								style="
									min-width: 230px;
									max-height: 300px;
									overflow-y: auto;
								">
								<v-list-item
									v-for="(vals, floor) in totalsByFloor"
									:key="'floor-total-' + floor"
									class="pb-2"
									style="
										display: flex;
										flex-direction: column;
										align-items: flex-start;
										white-space: normal;
									">
									<div
										class="text-caption font-weight-bold mb-1">
										{{ getFloorName(floor) }}
									</div>
									<div
										class="text-caption"
										style="color: rgba(0, 0, 0, 0.8)">
										<div>
											<strong>Suprafață:</strong>
											{{
												formatNumber(
													this,
													vals.area,
													false
												)
											}}
											m²
										</div>
										<div>
											<strong>Țeavă:</strong>
											{{
												formatNumber(
													this,
													vals.pipe,
													false
												)
											}}
											m
										</div>
										<div>
											<strong>Circuite:</strong>
											{{ vals.circuits }}
										</div>
										<div>
											<strong>Lungime Circuite:</strong>
											{{ vals.circuitLength.toFixed(2) }}
											m
										</div>
									</div>
								</v-list-item>
							</v-list>
						</v-menu>

						<!-- Apartamente -->
						<v-menu
							v-if="
								localShowApartments &&
								Object.keys(totalsByApartment).length > 0
							">
							<template #activator="{ props }">
								<v-btn
									v-bind="props"
									icon
									color="success"
									variant="text"
									:title="'Totaluri pe apartament'">
									<v-icon>mdi-checkerboard </v-icon>
								</v-btn>
							</template>

							<v-list
								class="py-2"
								style="
									min-width: 230px;
									max-height: 300px;
									overflow-y: auto;
								">
								<template
									v-for="(vals, apt) in totalsByApartment"
									:key="'apt-' + apt">
									<v-list-item
										class="pb-2"
										style="
											display: flex;
											flex-direction: column;
											align-items: flex-start;
											white-space: normal;
										">
										<div
											class="text-caption font-weight-bold mb-1">
											Apartament {{ apt }}
										</div>
										<div
											class="text-caption"
											style="color: rgba(0, 0, 0, 0.8)">
											<div>
												<strong>Suprafață:</strong>
												{{
													formatNumber(
														this,
														vals.area,
														false
													)
												}}
												m²
											</div>
											<div>
												<strong>Țeavă:</strong>
												{{
													formatNumber(
														this,
														vals.pipe,
														false
													)
												}}
												m
											</div>
											<div>
												<strong>Circuite:</strong>
												{{ vals.circuits }}
											</div>
											<div>
												<strong
													>Lungime Circuite:</strong
												>
												{{
													vals.circuitLength.toFixed(
														2
													)
												}}
												m
											</div>
										</div>
									</v-list-item>
								</template>
							</v-list>
						</v-menu>
					</template>
				</v-toolbar>
			</template>
		</v-data-table>
	</v-card>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import { formatNumber, showAlertModal } from "../../utils/utils";
import { FLOORS, FLOOR_MAP } from "../../IPA/Floors";
// import { APARTMENTS } from "../IPA/Apartments";
import ApartamentSelect from "../common/ApartamentSelect.vue";
import {
	makeGroupKey,
	getApartamentLabel,
	getUnitId,
} from "../../utils//apartmentUtils";

export default {
	name: "RoomsDetailsIPA",
	props: {
		location: {
			type: Object,
			required: true,
		},
		/** Effective compartmentation (from offer with fallback); use this instead of location.compartmentation. */
		effectiveCompartmentation: { type: Number, default: 0 },
		/** Effective customization (from offer with fallback); use this instead of location.customization. */
		effectiveCustomization: { type: Number, default: 0 },
		floorGroups: {
			type: Array,
			default: () => [],
		},
		autoCheckApartments: { type: Boolean, default: false },
		showApartments: { type: Boolean, default: false },
		insulationSubtype: { type: Object, default: () => ({}) },
	},
	components: {
		ApartamentSelect,
	},
	emits: [
		"save-location",
		"update-location",
		"update:showApartments",
		"recalc-needed",
		"markLocationChanged",
	],
	data() {
		return {
			initialRoomsSnapshot: [],
			showTotalDetails: false,
			localShowApartments: this.showApartments,
			// apartaments: APARTMENTS,
			confirmSnackbar: false,
			pendingDisableApartments: false,
			floors: FLOORS,
			rooms: [],
			groupBy: [{ key: "" }],
			groupOption: "",
			groupOptions: [
				{ title: "Negrupat", value: "" },
				{ title: "Etaj", value: "floor" },
				{ title: "Apartament", value: "apartament" },
			],
			resetDialog: false,
			descriptionDialog: false,
			change_description: null,

			isDescriptionNeeded: false,
			oldValues: {},
			isRoomAreaChanged: false,
			mountingStepOptions: [
				{ value: 5, label: "5 cm" },
				{ value: 10, label: "10 cm" },
				{ value: 15, label: "15 cm" },
				{ value: 20, label: "20 cm" },
			],

			roomNameOptions: [
				"Antreu",
				"Hol",
				"Baie",
				"Bucătărie",
				"Coridor",
				"Cămară",
				"Dormitor",
				"Camera de zi",
				"Cameră",
				"Garderobă",
				"Grup tehnico-sanitar",
				"Salon",
				"Sufragerie",
				"Living",
			],
			/** Names used when adding rooms (saved to history so they appear in dropdown). */
			roomNameHistory: [],
		};
	},
	computed: {
		/** Default room names + history of names saved when adding a room. */
		roomNameOptionsWithHistory() {
			const defaultSet = new Set(this.roomNameOptions);
			const combined = [...this.roomNameOptions];
			for (const name of this.roomNameHistory) {
				if (name && typeof name === "string" && !defaultSet.has(name.trim())) {
					combined.push(name.trim());
					defaultSet.add(name.trim());
				}
			}
			return combined;
		},
		vHeaders() {
			const headers = [
				{ title: "Denumire", align: "start", key: "name" },
				{
					title: "Nivel",
					align: "start",
					minWidth: "150px",
					key: "floor",
				},
				...(this.localShowApartments
					? [
							{
								title: "Apartament",
								align: "start",
								key: "apartament",
								minWidth: "200px",
							},
						]
					: []),
				{
					title: "Suprafață",
					align: "center",
					maxWidth: "130px",
					key: "area.sq_m",
				},
				{
					title: "Pas montaj",
					align: "start",
					nowrap: true,
					key: "mounting_step",
				},
				{
					title: "Total țeavă",
					align: "end",
					maxWidth: "130px",
					nowrap: true,
					key: "total_pipe",
				},
				{
					title: "Nr. circuite",
					align: "end",
					maxWidth: "120px",
					nowrap: true,
					key: "circuit_no",
				},
				{
					title: "Lungime circuit",
					align: "end",
					key: "circuit_length",
					maxWidth: "160px",
					nowrap: true,
				},
				{ title: "", align: "center", key: "actions", sortable: false },
			];

			headers.unshift({
				title: "Ajustare",
				align: "start",
				key: "isExcludedFromAdjust",
				width: "80px",
			});

			return headers;
		},

		totalArea() {
			return this.rooms.reduce(
				(sum, room) => sum + Number(room.area.sq_m || 0),
				0
			);
		},
		totalPipe() {
			return this.rooms.reduce(
				(sum, room) => sum + Number(room.total_pipe || 0),
				0
			);
		},
		totalCircuits() {
			return this.rooms.reduce(
				(sum, room) => sum + Number(room.circuit_no || 0),
				0
			);
		},
		totalCircuitsLength() {
			return this.rooms.reduce(
				(sum, room) => sum + Number(room.circuit_length || 0),
				0
			);
		},
		isNewVersionRequired() {
			let isRequired =
				this.rooms.some(
					room =>
						room.isDescriptionNeeded &&
						this.checkIsNewVersionRequired()
				) || this.isDescriptionNeeded;
			if (this.location.rooms.length < 1) {
				isRequired = false;
			}
			return isRequired;
		},
		// Calculate totals grouped by floor
		totalsByFloor() {
			const grouped = {};
			this.rooms.forEach(r => {
				const f = r.floor || 0;
				if (!grouped[f]) {
					grouped[f] = {
						area: 0,
						pipe: 0,
						circuits: 0,
						circuitLength: 0,
					};
				}
				grouped[f].area += Number(r.area?.sq_m || 0);
				grouped[f].pipe += Number(r.total_pipe || 0);
				grouped[f].circuits += Number(r.circuit_no || 0);
				grouped[f].circuitLength += Number(r.circuit_length || 0);
			});
			return grouped;
		},

		// Calculate totals grouped by apartment
		totalsByApartment() {
			const grouped = {};
			this.rooms.forEach(r => {
				const a = r.apartament || 0;
				if (!grouped[a]) {
					grouped[a] = {
						area: 0,
						pipe: 0,
						circuits: 0,
						circuitLength: 0,
					};
				}
				grouped[a].area += Number(r.area?.sq_m || 0);
				grouped[a].pipe += Number(r.total_pipe || 0);
				grouped[a].circuits += Number(r.circuit_no || 0);
				grouped[a].circuitLength += Number(r.circuit_length || 0);
			});
			return grouped;
		},
		groupSelectItems() {
			const base = [
				{ title: "Negrupat", value: "" },
				{ title: "Etaj", value: "floor" },
			];
			return this.localShowApartments
				? [...base, { title: "Apartament", value: "apartament" }]
				: base;
		},
	},
	async created() {
		if (this.location && Array.isArray(this.location.rooms)) {
			this.rooms = JSON.parse(JSON.stringify(this.location.rooms)).map(
				room => {
					const computedTotalPipe =
						room.total_pipe != null
							? room.total_pipe
							: Math.round(
									(100 / (room.mounting_step || 10)) *
										(room.area ? room.area.sq_m : 0) *
										100
								) / 100;
					const computedCircuitNo =
						room.circuit_no != null
							? room.circuit_no
							: Math.ceil(computedTotalPipe / 95);
					const dbCircuitLen = room.circuit_length_ipa;
					const computedCircuitLength =
						dbCircuitLen != null
							? Number(dbCircuitLen)
							: computedCircuitNo > 0
								? Math.round(
										(computedTotalPipe /
											computedCircuitNo) *
											100
									) / 100
								: 0;

					return {
						...room,
						localId: room.localId || uuidv4(),
						area: room.area || { sq_m: 0 },
						height: room.height || 250,
						floor_name: room.floor_name || "Parter",
						mounting_step: room.mounting_step || 10,
						total_pipe: computedTotalPipe,
						circuit_no: computedCircuitNo,
						circuit_length_ipa: dbCircuitLen,
						circuit_length: computedCircuitLength,
						isDescriptionNeeded: false,
						apartament: room.apartament ?? 0, // backend field
						group_key: makeGroupKey(
							room.apartament ?? 0,
							room.floor
						),

						isExcludedFromAdjust:
							room.isExcludedFromAdjust || false, // NEW
						_manualTotalPipe: !!room.is_total_pipe_edited,
						_manualCircuitNo: !!room.is_circuit_no_edited,
						_manualCircuitLength: !!room.is_circuit_length_edited,

						is_total_pipe_edited: !!room.is_total_pipe_edited,
						is_circuit_no_edited: !!room.is_circuit_no_edited,
						is_circuit_length_edited:
							!!room.is_circuit_length_edited,
					};
				}
			);
		}
		this.change_description = this.location.change_description || "";
		this.refreshInitialSnapshot();
	},
	mounted() {
		const comp = Number(this.effectiveCompartmentation ?? this.location?.compartmentation ?? 0);
		const cust = Number(this.effectiveCustomization ?? this.location?.customization ?? 0);

		if (comp === 2 || cust === 2) {
			this.localShowApartments = true;
		} else if (
			Array.isArray(this.location?.rooms) &&
			this.location.rooms.some(
				r => r.apartament && r.apartament !== 0 && r.apartament !== "0"
			)
		) {
			this.localShowApartments = true;
		}
		// Default group by floor if there is more than one floor
		const uniqueFloors = [
			...new Set(
				(this.location.rooms || [])
					.map(r => r.floor)
					.filter(f => f !== null && f !== undefined)
			),
		];

		if (uniqueFloors.length > 1) {
			this.groupOption = "floor";
			this.changeGroupingProperty("floor");
		}
	},
	methods: {
		/**
		 * Builds a stable, comparable snapshot of a room for "net change" detection.
		 *
		 * @param {Room} room - The room to snapshot.
		 * @returns {Object} snapshot - Plain object containing only persisted, domain-relevant fields.
		 *
		 * @description
		 * Used to compare current room state against the initial baseline.
		 * Excludes UI/ephemeral fields (e.g., localId, manual highlight flags)
		 * and normalizes values (Number/boolean/String) to keep comparisons deterministic.
		 */

		getRoomComparableSnapshot(room) {
			return {
				id: room.id ?? null,
				name: room.name ?? null,
				floor: Number(room.floor ?? 0),
				apartament: String(room.apartament ?? "0"),
				area_sq_m: Number(room.area?.sq_m ?? 0),
				mounting_step: Number(room.mounting_step ?? 10),
				total_pipe: Number(room.total_pipe ?? 0),
				circuit_no: Number(room.circuit_no ?? 0),
				circuit_length: Number(room.circuit_length ?? 0),
				isExcludedFromAdjust: !!room.isExcludedFromAdjust,

				is_total_pipe_edited: !!room.is_total_pipe_edited,
				is_circuit_no_edited: !!room.is_circuit_no_edited,
				is_circuit_length_edited: !!room.is_circuit_length_edited,
			};
		},
		/**
		 * Checks whether current rooms differ from the initial baseline (net changes).
		 *
		 * @returns {boolean} - True if any persistent room values changed vs. baseline.
		 *
		 * @description
		 * Maps rooms through getRoomComparableSnapshot() and performs a deep compare
		 * via JSON stringify. Safe here because snapshots are small, flat, and normalized.
		 */
		hasNetRoomChanges() {
			const current = this.rooms.map(r =>
				this.getRoomComparableSnapshot(r)
			);
			return (
				JSON.stringify(current) !==
				JSON.stringify(this.initialRoomsSnapshot)
			);
		},
		/**
		 * Synchronizes change/versioning flags based on net changes vs. baseline.
		 *
		 * @returns {void}
		 *
		 * @description
		 * - For brand-new locations (version === 0), we never require a version description.
		 *   We keep only a lightweight "unsaved changes" signal via isRoomAreaChanged.
		 * - For existing locations, isDescriptionNeeded is driven strictly by net diff.
		 *
		 * This prevents "phantom" versioning when the user edits and later resets
		 * back to the initial state.
		 */
		syncChangeFlagsWithNetState() {
			const changed = this.hasNetRoomChanges();

			if (Number(this.location?.version || 0) === 0) {
				this.isDescriptionNeeded = false;

				this.isRoomAreaChanged = changed;
				this.rooms.forEach(r => (r.isDescriptionNeeded = false));

				return;
			}

			this.isDescriptionNeeded = changed;
			this.isRoomAreaChanged = changed;
			this.rooms.forEach(r => (r.isDescriptionNeeded = changed));
		},

		/**
		 * Gets the unit key for a room based on customization type.
		 * @param {Object} room
		 * @returns {string|number}
		 */
		getRoomUnitKey(room) {
			const custType = Number(this.location?.customization ?? 0);
			if (custType === 0) return 0;
			if (custType === 1) return String(room.floor ?? 0);
			if (custType === 2) {
				return getUnitId(room.apartament ?? room.apartment ?? "0");
			}
			return 0;
		},

		/**
		 * Checks if mounting step is disabled for a specific room.
		 * @param {Object} room
		 * @returns {boolean}
		 */
		isMountingStepDisabled(room) {
			const unitKey = this.getRoomUnitKey(room);
			const subtype =
				this.insulationSubtype[unitKey] ?? this.insulationSubtype[0];
			return subtype === 3;
		},

		/**
		 * Refreshes the initial baseline snapshot to match the current saved state.
		 *
		 * @returns {void}
		 *
		 * @description
		 * Call this after a successful save so future net-change checks compare
		 * against the newly persisted rooms, not the original load.
		 */

		refreshInitialSnapshot() {
			this.initialRoomsSnapshot = this.rooms.map(r =>
				this.getRoomComparableSnapshot(r)
			);
		},
		/**
		 * Sets a manual-edit UI flag if the room field value actually changed.
		 *
		 * @param {Room} item - The room being edited.
		 * @param {string} fieldKey - Field key being tracked (e.g., "total_pipe").
		 * @param {string} flagKey - UI/manual flag to set when changed (e.g., "_manualTotalPipe").
		 * @returns {void}
		 *
		 * @description
		 * Compares the stored old value from oldValues against the new value.
		 * If different, marks the corresponding manual flag so the UI can highlight it.
		 */
		setManualFlagIfChanged(item, fieldKey, flagKey) {
			const key = `${item.localId}.${fieldKey}`;
			const oldVal = this.oldValues[key];

			if (oldVal == null) return;

			const newVal = JSON.stringify(item[fieldKey]);

			if (newVal !== oldVal) {
				item[flagKey] = true;
			}
		},
		clearOldValue(item, fieldKey) {
			const key = `${item.localId}.${fieldKey}`;
			delete this.oldValues[key];
		},

		/**
		 * Validates that each apartment is assigned to only one floor.
		 *
		 * @returns {boolean} - Returns true if all apartments are unique per floor, false otherwise.
		 *
		 * @description
		 * Iterates through all rooms, grouping them by apartment number and collecting
		 * the floors they appear on. If an apartment appears on more than one floor,
		 * an alert is shown and validation fails.
		 */
		validateUniqueApartmentPerFloor() {
			const rooms = this.rooms || [];

			const seen = {};

			for (const r of rooms) {
				const apt = String(r.apartament ?? r.apartment ?? "")
					.trim()
					.toLowerCase();
				const floor = Number(r.floor ?? 1);
				if (!apt || apt === "0") continue;

				if (!seen[apt]) seen[apt] = new Set();
				seen[apt].add(floor);
			}

			let invalid = null;
			for (const apt in seen) {
				if (seen[apt].size > 1) {
					invalid = { apt, floors: [...seen[apt]] };
					break;
				}
			}

			if (invalid) {
				const floorLabels = invalid.floors
					.map(f => this.getFloorName(f))
					.join(", ");

				showAlertModal(
					this.$store,
					`Apartamentul <b>${invalid.apt}</b> este setat pe mai multe etaje (${floorLabels}). 
                        Fiecare apartament trebuie să aparțină unui singur etaj.`,
					"warning",
					8000
				);

				return false;
			}

			return true;
		},
		/**
		 * Toggles (expands or collapses) all group rows in the grouped data table.
		 *
		 * @description
		 * Simulates a click on every group toggle button (via `$refs`)
		 * to expand collapsed groups or collapse expanded ones.
		 */
		toggleAllGroups() {
			Object.values(this.$refs).forEach(ref => {
				ref?.$el?.click();
			});
		},
		/**
		 * Expands all collapsed groups in the grouped data table.
		 *
		 * @description
		 * Iterates through all group toggle buttons (via `$refs`)
		 * and triggers a click only if the group is currently closed.
		 */
		openAllGroups() {
			Object.values(this.$refs).forEach(ref => {
				const items = Array.isArray(ref) ? ref : [ref];
				items.forEach(target => {
					if (target?.$el && !target.$attrs?.["data-open"]) {
						target.$el.click();
					}
				});
			});
		},
		/**
		 * Collapses all expanded groups in the grouped data table.
		 *
		 * @description
		 * Iterates through all group toggle buttons (via `$refs`)
		 * and triggers a click only if the group is currently open.
		 */
		closeAllGroups() {
			Object.values(this.$refs).forEach(ref => {
				const items = Array.isArray(ref) ? ref : [ref];
				items.forEach(target => {
					if (target?.$el && target.$attrs?.["data-open"]) {
						target.$el.click();
					}
				});
			});
		},
		/**
		 * Generates formatted label strings for group totals.
		 *
		 * @param {Object} groupTotals - The totals object containing area, pipe, circuits, and circuit length.
		 * @param {number} groupTotals.area - The total area in square meters.
		 * @param {number} groupTotals.pipe - The total pipe length in meters.
		 * @param {number} groupTotals.circuits - The total number of circuits.
		 * @param {number} groupTotals.circuitLength - The total circuit length in meters.
		 * @returns {Object} - A key-value map of labels to formatted strings.
		 */
		groupTotalLabels(groupTotals) {
			return {
				Suprafață: `${this.formatNumber(
					this,
					groupTotals.area,
					false
				)} m²`,
				Țeavă: `${this.formatNumber(this, groupTotals.pipe, false)} m`,
				Circuite: groupTotals.circuits,
				"Lungime circuite": `${groupTotals.circuitLength.toFixed(2)} m`,
			};
		},
		/**
		 * Extracts the apartment key from a combined group key (e.g., "A1|2").
		 *
		 * @param {string|number} value - The value or combined key to extract from.
		 * @returns {string|number} - The apartment part if combined, otherwise the original value.
		 */
		getApartmentKey(value) {
			if (typeof value === "string" && value.includes("|")) {
				return value.split("|")[0];
			}
			return value;
		},

		/**
		 * Enables manual editing of a circuit's length for a given item.
		 * Ensures reactivity by using Vue's $set when available.
		 *
		 * @param {Object} item - The circuit item to modify.
		 * @param {boolean} [item._manualCircuitLength] - Internal flag indicating manual edit mode.
		 */
		startManualCircuitLengthEdit(item) {
			if (!item._manualCircuitLength) {
				this.$set
					? this.$set(item, "_manualCircuitLength", true)
					: (item._manualCircuitLength = true);
			}
		},

		/**
		 * Enables manual editing mode for the circuit number of a room.
		 *
		 * @param {Object} item - The room object being edited.
		 * @param {boolean} [item._manualCircuitNo] - Internal flag indicating whether the circuit number is being edited manually.
		 *
		 * @description
		 * Sets the `_manualCircuitNo` flag to `true` on the given room object.
		 * This flag is used to temporarily disable automatic recalculation of the circuit length
		 * when the user manually modifies the number of circuits.
		 *
		 * Uses Vue's `$set` method if available to preserve reactivity.
		 *
		 * @example
		 * // Called when a user starts typing in the "Nr. circuite" field:
		 * startManualCircuitNoEdit(room);
		 */

		startManualCircuitNoEdit(item) {
			if (!item._manualCircuitNo) {
				this.$set
					? this.$set(item, "_manualCircuitNo", true)
					: (item._manualCircuitNo = true);
			}
		},
		/**
		 * Handles toggling of apartment visibility.
		 * If turned off, triggers confirmation before disabling.
		 *
		 * @param {boolean} value - True to show apartments, false to request confirmation before hiding.
		 */
		onToggleApartments(value) {
			if (value) {
				this.localShowApartments = true;
				this.$emit("update:showApartments", true);
				this.$emit("markLocationChanged");

				return;
			}

			this.pendingDisableApartments = true;
			this.confirmSnackbar = true;
		},
		/**
		 * Cancels the disable-apartments confirmation dialog.
		 * Restores the apartments view state.
		 */
		cancelDisableApartments() {
			this.confirmSnackbar = false;
			this.pendingDisableApartments = false;
			this.localShowApartments = true;
			this.$emit("update:showApartments", true);
		},

		/**
		 * Confirms disabling of apartments.
		 * Updates location and room data accordingly,
		 * emitting an updated location object if compartmentation or customization values change.
		 */
		confirmDisableApartments() {
			this.confirmSnackbar = false;
			this.pendingDisableApartments = false;
			this.localShowApartments = false;
			this.$emit("update:showApartments", false);
			this.rooms.forEach(r => (r.apartament = 0));

			const comp = this.effectiveCompartmentation ?? this.location?.compartmentation ?? 0;
			const cust = this.effectiveCustomization ?? this.location?.customization ?? 0;
			const newComp = comp === 2 ? 0 : comp;
			const newCust = cust === 2 ? 1 : cust;

			if (newComp !== comp || newCust !== cust) {
				const updatedLocation = {
					...this.location,
					compartmentation: newComp,
					customization: newCust,
					rooms: this.rooms.map(r => ({ ...r, apartament: "0" })),
				};

				this.$emit("update-location", updatedLocation);
				this.$emit("markLocationChanged");
			} else {
				this.emitLocationUpdate(true, false);
			}
		},
		/**

        * Determine if new version is required based on existing offers and change flags.
        * @returns {boolean}
        */
		checkIsNewVersionRequired() {
			const offers = this.$store.state.fetchedClientOffers || [];

			// No client offers at all -> do not force description/versioning
			if (!offers.length) {
				this.isDescriptionNeeded = false;
				return false;
			}

			// Flatten root offers + nested offers (keeps IPA behaviour)
			const allOffers = [];
			for (const offer of offers) {
				allOffers.push(offer);
				if (Array.isArray(offer.offers) && offer.offers.length > 0) {
					allOffers.push(...offer.offers);
				}
			}

			const locationId = this.location?.id;
			if (locationId == null) {
				this.isDescriptionNeeded = false;
				return false;
			}

			const locationOffers = allOffers.filter(
				o =>
					String(
						o.location?.id || o.location_data?.id || o.location
					) === String(locationId)
			);

			// No offers for this specific location -> no description needed
			if (!locationOffers.length) {
				this.isDescriptionNeeded = false;
				return false;
			}

			const allAllowed = locationOffers.every(
				o => o.type === 1 || o.type === 4
			);

			const hasChanges =
				this.isRoomAreaChanged || this.location.isLocationChanged;

			// If everything is allowed or nothing actually changed, do not mark description as needed
			if (allAllowed || !hasChanges) {
				this.isDescriptionNeeded = false;
				return false;
			}

			// There are relevant offers, disallowed types and real changes -> description required
			this.isDescriptionNeeded = true;
			return true;
		},

		formatNumber,
		/**
		 * Handler for toggling the Ajustare (exclude from adjust) flag.
		 * Marks room and location as changed and emits update.
		 * @param {Object} item - The room object.
		 */
		onToggleAdjust(item) {
			item.isDescriptionNeeded = true;
			this.isDescriptionNeeded = true;
			this.emitLocationUpdate();
		},
		/**
		 * Change the grouping column and prune empty rooms.
		 * @param {"apartament"|"floor"|string} column
		 * @returns {void}
		 */
		changeGroupingProperty(column) {
			if (column === "apartament") {
				this.groupBy = [{ key: "apartament" }];
			} else if (column === "floor") {
				this.groupBy = [{ key: "floor" }];
			} else {
				this.groupBy = [];
			}
			this.deleteEmptyRooms();
			this.$nextTick(() => this.openAllGroups());
		},

		/**
		 * Update a field on a room, recomputing derived values when needed and emitting update.
		 * @param {Room} item
		 * @param {string} fieldKey - e.g. "area", "mounting_step", "floor", "apartament", "total_pipe", "circuit_no", "name".
		 * @param {boolean} [isArea=false] - Force area handling when using nested inputs.
		 * @returns {void}
		 */

		updateField(item, fieldKey, isArea = false) {
			if (!item) return;
			const markChanged = () => {
				if (this.location.version > 0) {
					item.isDescriptionNeeded = true;
					this.isDescriptionNeeded = true;
				}
				this.emitLocationUpdate();
				this.$emit("recalc-needed");
			};

			if (fieldKey === "area" || isArea) {
				// ensure number
				item.area.sq_m = Number(item.area?.sq_m || 0);
				item.is_total_pipe_edited = false;
				item.is_circuit_no_edited = false;
				item.is_circuit_length_edited = false;

				// highlight UI off
				item._manualTotalPipe = false;
				item._manualCircuitNo = false;
				item._manualCircuitLength = false;
				this.calculateDerivedRoomValues(item);
				this.isRoomAreaChanged = true;
				return markChanged();
			}

			if (fieldKey === "mounting_step") {
				item.mounting_step = Number(item.mounting_step || 10);
				item.is_total_pipe_edited = false;
				item.is_circuit_no_edited = false;
				item.is_circuit_length_edited = false;

				// highlight UI off
				item._manualTotalPipe = false;
				item._manualCircuitNo = false;
				item._manualCircuitLength = false;

				this.calculateDerivedRoomValues(item);
				return markChanged();
			}

			if (fieldKey === "floor") {
				item.floor = Number(item.floor);
				item.floor_name = this.getFloorName(item.floor); // from FLOOR_MAP
				item.group_key = makeGroupKey(
					item.apartament ?? null,
					item.floor
				);
				return markChanged();
			}
			if (fieldKey === "apartament") {
				if (
					typeof item.apartament === "object" &&
					item.apartament !== null
				) {
					item.apartament = String(
						item.apartament.value ?? item.apartament.name ?? ""
					);
				} else {
					item.apartament = item.apartament
						? String(item.apartament)
						: "0";
				}

				item.group_key = makeGroupKey(item.apartament, item.floor);
				const isValid = this.validateUniqueApartmentPerFloor();
				if (!isValid) {
					item.apartament = null;
					item.group_key = makeGroupKey(null, item.floor);
				}
				return markChanged();
			}

			if (fieldKey === "total_pipe") {
				item.total_pipe = Number(item.total_pipe || 0);
				const key = `${item.localId}.total_pipe`;
				const oldVal = this.oldValues[key]; // string JSON
				const newVal = JSON.stringify(item.total_pipe);

				if (oldVal != null && newVal === oldVal) {
					this.clearOldValue(item, "total_pipe");
					return;
				}
				this.setManualFlagIfChanged(
					item,
					"total_pipe",
					"_manualTotalPipe"
				);
				if (item._manualTotalPipe) item.is_total_pipe_edited = true;
				item.is_circuit_no_edited = false;
				item._manualCircuitNo = false;

				if (item.total_pipe === 0) {
					item.circuit_no = 0;
					item.circuit_length = 0;
				} else {
					item.circuit_no = Math.ceil(item.total_pipe / 95);

					item.is_circuit_length_edited = false;
					item._manualCircuitLength = false;
					item.circuit_length =
						item.circuit_no > 0
							? Math.round(
									(item.total_pipe / item.circuit_no) * 100
								) / 100
							: 0;
					item.circuit_length_ipa = item.circuit_length;
				}

				this.clearOldValue(item, "total_pipe");

				return markChanged();
			}

			if (fieldKey === "circuit_no") {
				item.circuit_no = Number(item.circuit_no || 0);
				this.setManualFlagIfChanged(
					item,
					"circuit_no",
					"_manualCircuitNo"
				);
				if (item._manualCircuitNo) item.is_circuit_no_edited = true;
				// if (!item.is_circuit_length_edited) {
				//     item.circuit_length =
				//         item.circuit_no > 0
				//             ? Math.round(
				//                   (Number(item.total_pipe || 0) /
				//                       item.circuit_no) *
				//                       100
				//               ) / 100
				//             : 0;
				// }

				this.clearOldValue(item, "circuit_no");
				return markChanged();
			}
			if (fieldKey === "circuit_length") {
				item.circuit_length = Number(item.circuit_length || 0);
				this.setManualFlagIfChanged(
					item,
					"circuit_length",
					"_manualCircuitLength"
				);
				if (item._manualCircuitLength)
					item.is_circuit_length_edited = true;

				// sync IPA field
				item.circuit_length_ipa = item.circuit_length;
				this.clearOldValue(item, "circuit_length");
				return markChanged();
			}
			this.calculateDerivedRoomValues(item);
			// name or other simple fields
			return markChanged();
		},
		getFloorName(v) {
			return FLOOR_MAP[Number(v)] ?? "Nepozitionat";
		},
		/**
		 * Get a display name for the current group value based on `groupBy`.
		 * @param {string|number|null} value - `group_key`, floor, or apartament value.
		 * @returns {string}
		 */
		getGroupName(value) {
			if (typeof value === "string" && value.includes("|")) {
				const [apartament, floor] = value.split("|");
				const floorName = this.getFloorName(Number(floor));
				return `${apartament}  (${floorName})`;
			}

			const key = this.groupBy[0]?.key;
			if (key === "floor") return this.getFloorName(value);
			if (key === "apartament") return getApartamentLabel(value);
			return "Negrupat";
		},
		/**
		 * Handles the focus event on input fields by selecting the input content
		 * and storing the original value of the field for future comparison.
		 *
		 * @param {FocusEvent} event - The DOM focus event triggered by the input.
		 * @param {Object} item - The data object (e.g., a room entry).
		 * @param {string} fieldKey - The field key using dot notation (e.g., "name", "area.sq_m").
		 */
		handleFocusWithOldValue(event, item, fieldKey) {
			if (event?.target?.select) {
				event.target.select();
			}
			this.storeOldValue(item, fieldKey);
		},
		/**
		 * Store the original value of a field to `oldValues`.
		 * @param {Room} item
		 * @param {string} fieldKey
		 * @returns {void}
		 */
		storeOldValue(item, fieldKey) {
			const key = `${item.localId}.${fieldKey}`;
			if (!this.oldValues[key]) {
				this.oldValues[key] = JSON.stringify(item[fieldKey]);
			}
		},

		/**
		 * Create and focus a new empty room after the current last room's context.
		 * @returns {void}
		 */
		addRoom() {
			const newRoomId = uuidv4();
			const lastRoom = this.rooms[this.rooms.length - 1];
			const apartament = lastRoom?.apartament ?? 0;
			const area = Number(lastRoom?.area?.sq_m || 0);

			if (lastRoom && area <= 0) {
				showAlertModal(
					this.$store,
					"Suprafața trebuie să fie mai mare decât 0.",
					"warning",
					5000
				);
				return null;
			}
			const floor = lastRoom?.floor ?? 2;
			// Save last room's name to history when adding a new room (client requirement: istoric nume încăperi)
			const lastRoomName =
				lastRoom?.name != null && String(lastRoom.name).trim()
					? String(lastRoom.name).trim()
					: null;
			if (
				lastRoomName &&
				!this.roomNameOptions.includes(lastRoomName) &&
				!this.roomNameHistory.includes(lastRoomName)
			) {
				this.roomNameHistory.push(lastRoomName);
			}
			// New room always starts without a name (client requirement: do not copy previous room name)
			const name = null;

			const newRoom = {
				localId: newRoomId,
				name,
				area: { sq_m: 0 },
				height: 250,
				floor,
				apartament,
				floor_name: this.getFloorName(floor),
				mounting_step: 10,
				total_pipe: 0,
				circuit_no: 0,
				circuit_length: 0,
				_manualCircuitLength: false,
			};
			newRoom.group_key = makeGroupKey(newRoom.apartament, newRoom.floor);

			this.rooms.push(newRoom);
			this.emitLocationUpdate();

			return;
		},
		/**
		 * Recalculate derived pipe/circuit values for a room from its area & mounting step.
		 * @param {Room} room
		 * @returns {void}
		 */
		calculateDerivedRoomValues(room) {
			if (!room) return;

			const preservedArea = room.area.sq_m;
			const preservedMountingStep = room.mounting_step;

			if (!room.is_total_pipe_edited) {
				room.total_pipe =
					Math.round(
						(100 / preservedMountingStep) * preservedArea * 100
					) / 100;
			}
			if (!room.is_circuit_no_edited) {
				room.circuit_no =
					preservedArea > 0 ? Math.ceil(room.total_pipe / 95) : 0;
			}
			if (!room.is_circuit_length_edited && !room.is_circuit_no_edited) {
				room.circuit_length =
					room.circuit_no > 0
						? Math.round(
								(room.total_pipe / room.circuit_no) * 100
							) / 100
						: 0;
				room.circuit_length_ipa = room.circuit_length;
			}
		},
		/**
		 * Deletes a room by its local identifier.
		 *
		 * @param {string|number} localId - The unique local identifier of the room to delete.
		 * @returns {void}
		 *
		 * @description
		 * - Finds the room in `this.rooms` and removes it if found.
		 * - Marks `isDescriptionNeeded` as `true` to flag changes.
		 * - Emits an updated location object via `emitLocationUpdate()`.
		 */
		deleteRoom(localId) {
			const idx = this.rooms.findIndex(r => r.localId === localId);
			if (idx !== -1) {
				if (this.rooms[idx].id && this.isDescriptionNeeded) {
					this.isDescriptionNeeded = true;
					this.newVersion = true;
				}
				this.rooms.splice(idx, 1);

				this.emitLocationUpdate();
				this.$emit("markLocationChanged");
			}
		},
		/**
		 * Clears all manual/edited flags on a room so derived values can be recalculated.
		 *
		 * @param {Room} room - The room object whose flags are reset.
		 * @returns {void}
		 *
		 * @description
		 * Resets both:
		 * - persistent backend flags (is_*_edited) that block auto recompute
		 * - UI-only/manual flags (_manual*) used for highlighting/UX
		 *
		 * Intended to be called before calculateDerivedRoomValues().
		 */
		clearRoomManualFlags(room) {
			room.is_total_pipe_edited = false;
			room.is_circuit_no_edited = false;
			room.is_circuit_length_edited = false;

			room._manualTotalPipe = false;
			room._manualCircuitNo = false;
			room._manualCircuitLength = false;
		},
		/**
		 * Builds a normalized snapshot of a room’s relevant fields for reset/change detection.
		 *
		 * @param {Room} room - The room object to snapshot.
		 * @returns {Object} snapshot - Plain object with comparable values.
		 *
		 * @description
		 * Extracts only the fields that matter for deciding whether a reset
		 * actually changed anything:
		 * - derived numeric values (pipe, circuits, length)
		 * - persistent edit flags
		 * - UI/manual edit flags
		 *
		 * All values are normalized (Number / boolean) so comparisons are stable.
		 */
		getRoomResetSnapshot(room) {
			return {
				total_pipe: Number(room.total_pipe || 0),
				circuit_no: Number(room.circuit_no || 0),
				circuit_length: Number(room.circuit_length || 0),

				is_total_pipe_edited: !!room.is_total_pipe_edited,
				is_circuit_no_edited: !!room.is_circuit_no_edited,
				is_circuit_length_edited: !!room.is_circuit_length_edited,

				_manualTotalPipe: !!room._manualTotalPipe,
				_manualCircuitNo: !!room._manualCircuitNo,
				_manualCircuitLength: !!room._manualCircuitLength,
			};
		},

		/**
		 * Compares two room snapshots to determine if a reset produced changes.
		 *
		 * @param {Object} before - Snapshot taken before reset.
		 * @param {Object} after - Snapshot taken after reset.
		 * @returns {boolean} - True if snapshots differ, false otherwise.
		 *
		 * @description
		 * Uses JSON string compare because snapshots are small, flat, and normalized.
		 * If you later add nested objects, keep them normalized or swap to a deepEqual.
		 */

		didRoomChange(before, after) {
			return JSON.stringify(before) !== JSON.stringify(after);
		},
		/**
		 * Resets the state of a single room by recalculating its derived values.
		 *
		 * @param {string|number} localId - The unique local identifier of the room to reset.
		 * @returns {void}
		 *
		 * @description
		 * - Recalculates derived values for the specified room using `calculateDerivedRoomValues()`.
		 * - Marks the room’s `isDescriptionNeeded` and global `isDescriptionNeeded` as `true`.
		 * - Emits updated location data.
		 */
		resetOneRoom(localId) {
			const idx = this.rooms.findIndex(r => r.localId === localId);
			if (idx === -1) return;

			const room = this.rooms[idx];

			const before = this.getRoomResetSnapshot(room);

			this.clearRoomManualFlags(room);
			this.calculateDerivedRoomValues(room);

			const after = this.getRoomResetSnapshot(room);
			const changed = this.didRoomChange(before, after);

			if (changed) {
				this.syncChangeFlagsWithNetState();
				this.emitLocationUpdate();
				if (this.isDescriptionNeeded) this.$emit("recalc-needed");
			} else {
				this.emitLocationUpdate(false, false);
			}
		},
		/**
		 * Resets all rooms to their derived (auto-calculated) values.
		 *
		 * @returns {void}
		 *
		 * @description
		 * For each room:
		 * - Takes a "before" snapshot of reset-relevant values.
		 * - Clears manual/edited flags so auto recalculation is allowed.
		 * - Recomputes derived values from area + mounting step.
		 * - Takes an "after" snapshot and checks if anything actually changed.
		 * - Marks room.isDescriptionNeeded only when the reset produced a real change.
		 *
		 * After processing all rooms:
		 * - syncChangeFlagsWithNetState() updates global flags based on net diffs
		 *   vs. the initial baseline (prevents phantom versioning).
		 * - Closes the confirmation dialog.
		 * - Emits the updated location once.
		 * - Triggers recalc-needed only if the location is truly changed.
		 */
		resetAllRooms() {
			this.rooms.forEach(room => {
				const before = this.getRoomResetSnapshot(room);

				this.clearRoomManualFlags(room);
				this.calculateDerivedRoomValues(room);

				const after = this.getRoomResetSnapshot(room);
				const changed = this.didRoomChange(before, after);

				room.isDescriptionNeeded = changed;
			});

			this.syncChangeFlagsWithNetState();

			this.resetDialog = false;
			this.emitLocationUpdate();
			if (this.isDescriptionNeeded) this.$emit("recalc-needed");
		},
		/**
		 * Handles navigation between room fields when the user presses the Tab key.
		 *
		 * @param {Object} item - The room object being edited.
		 * @param {string} field - The field name triggering the Tab event (e.g., `"area"`, `"name"`).
		 * @returns {void}
		 *
		 * @description
		 * - Ensures correct update ordering between `blur` and `tab` events by calling `updateField()`.
		 * - Focuses the next appropriate input field based on the field type.
		 * - Automatically adds a new room if the last room’s name is completed after tabbing from `area`.
		 */
		handleTab(item, field) {
			// workaround the issue of not updating correctly since the blur event triggers after the tab event
			if (field === "area") {
				this.updateField(item, field, true, true);
			} else if (field === "name") {
				this.updateField(item, field, false, false);
			}

			let roomRef;
			const lastIndex = this.rooms.length - 1;

			switch (field) {
				case "area":
					if (item.name === "") {
						roomRef = this.$refs["roomName" + item.localId];
						const cmp = Array.isArray(roomRef) ? roomRef[0] : roomRef;
						const el = cmp?.$el?.querySelector?.("input") || cmp;
						if (el && typeof el.focus === "function") el.focus();
					} else if (this.rooms[lastIndex].localId === item.localId) {
						const beforeLen = this.rooms.length;
						this.addRoom();
						if (this.rooms.length > beforeLen) {
							this.focusLastRoomName();
						}
					}
					break;
				case "name":
					roomRef = this.$refs["roomArea" + item.localId];
					roomRef.focus();
					break;
			}
		},
		/**
		 * Focus the "Name" input of the last room row.
		 *
		 * Purpose:
		 * After adding a new room (via Tab or button), we want the keyboard focus to land
		 * on the new row’s name field so the user can continue typing without using the mouse.
		 *
		 * How it works:
		 * - Grabs the last room from `this.rooms`.
		 * - Resolves the Vuetify ref `roomName<localId>` (which may be a component instance
		 *   or an array because of v-for) and finds the underlying native `<input>`/`<textarea>`.
		 * - Calls `.focus()` and `.select()` to place the caret and select any existing text.
		 * - If the input isn’t in the DOM yet (rendering is async), retries with a small delay.
		 *
		 * Timing:
		 * Uses `this.$nextTick()` to wait for the DOM update, then retries up to `retries`
		 * times with ~30ms intervals until the element is focusable.
		 *
		 * @param {number} [retries=10] Maximum number of retry attempts to obtain and focus the input.
		 * @returns {void}
		 */
		focusLastRoomName(retries = 10) {
			const last = this.rooms[this.rooms.length - 1];
			if (!last) return;

			this.$nextTick(() => {
				const ref = this.$refs["roomName" + last.localId];
				const cmp = Array.isArray(ref) ? ref[0] : ref;
				const el = cmp?.$el?.querySelector?.("input,textarea") || cmp;

				if (el && typeof el.focus === "function") {
					el.focus();
					el.select?.();
				} else if (retries > 0) {
					setTimeout(() => this.focusLastRoomName(retries - 1), 30);
				}
			});
		},

		/**
		 * Deletes all rooms with an area of zero square meters.
		 *
		 * @returns {void}
		 *
		 * @description
		 * - Filters rooms with `area.sq_m === 0`.
		 * - Removes each empty room using `deleteRoom()`.
		 */
		deleteEmptyRooms() {
			const emptyRooms = this.rooms.filter(r => r.area.sq_m === 0);
			emptyRooms.forEach(r => {
				this.deleteRoom(r.localId);
			});
		},
		/**
		 * Emits the current location data with updated room information.
		 *
		 * @param {boolean} [isLocationChanged=false] - Whether the location has been modified.
		 * @param {boolean} [forceNewVersion=false] - Whether to force saving as a new version.
		 * @returns {void}
		 *
		 * @emits update-location - Emits the updated location object.
		 *
		 * @description
		 * - Constructs a clean `updatedLocation` object with current room details.
		 * - Includes derived fields and ensures correct serialization of nested objects.
		 * - Sets `newVersion`, `change_description`, and change flags.
		 * - Emits `update-location` with the updated object.
		 */
		emitLocationUpdate(isLocationChanged, forceNewVersion = false) {
			const comp = this.effectiveCompartmentation ?? this.location?.compartmentation ?? 0;
			const cust = this.effectiveCustomization ?? this.location?.customization ?? 0;
			const updatedLocation = JSON.parse(JSON.stringify(this.location));
			updatedLocation.compartmentation = comp;
			updatedLocation.customization = cust;
			updatedLocation.isDescriptionNeeded = this.isDescriptionNeeded;

			updatedLocation.rooms = JSON.parse(JSON.stringify(this.rooms)).map(
				room => {
					return {
						id: room.id,
						name: room.name,
						floor: room.floor,
						area: room.area,
						mounting_step: room.mounting_step,
						total_pipe: room.total_pipe,
						circuit_no: room.circuit_no,
						circuit_length_ipa: room.circuit_length,
						// flags persistente
						is_total_pipe_edited: !!room.is_total_pipe_edited,
						is_circuit_no_edited: !!room.is_circuit_no_edited,
						is_circuit_length_edited:
							!!room.is_circuit_length_edited,
						isExcludedFromAdjust: room.isExcludedFromAdjust,
						apartament:
							room.apartament != null
								? String(room.apartament)
								: null,

						pipe_diameter: room.pipe_diameter ?? null,
						distributor: room.distributor ?? null,
						metal_box: room.metal_box ?? null,
						temperature_regulation:
							room.temperature_regulation ?? null,
						automation: room.automation ?? null,
						reinforcement_mesh: room.reinforcement_mesh ?? null,
						insulationType: room.insulationType ?? null,
						additionalInsulations:
							room.additionalInsulations ?? null,
						tackerFoil: room.tackerFoil ?? null,
						isDescriptionNeeded: room.isDescriptionNeeded ?? false,
					};
				}
			);
			if (forceNewVersion) {
				updatedLocation.newVersion = true;

				updatedLocation.parent = this.location.id;
				delete updatedLocation.id;
			} else {
				updatedLocation.newVersion = this.isNewVersionRequired;
			}

			updatedLocation.change_description = this.change_description || "";
			updatedLocation.isLocationChanged = true;
			this.isRoomAreaChanged = true;

			this.$emit("update-location", updatedLocation);
		},
		setDescriptionDialogTrue() {
			this.descriptionDialog = true;
		},
		setDescriptionDialogFalse() {
			this.descriptionDialog = false;
		},
		/**
		 * Saves the current location state, with support for versioning and empty-room cleanup.
		 *
		 * @returns {void}
		 *
		 * @emits save-location - Emits the current `change_description` text when saving.
		 *
		 * @description
		 * - If `descriptionDialog` is open: saves as a new version, deletes empty rooms,
		 *   emits updates, then closes the dialog.
		 * - If `isNewVersionRequired` is true: opens the dialog to request user confirmation.
		 * - Otherwise: performs a simple save (without versioning).
		 */
		saveLocation() {
			if (this.descriptionDialog) {
				this.deleteEmptyRooms();

				this.emitLocationUpdate(true, true);

				this.$emit("save-location", this.change_description);

				this.change_description = "";
				this.isDescriptionNeeded = false;
				this.descriptionDialog = false;
				return;
			}

			if (this.isNewVersionRequired) {
				this.setDescriptionDialogTrue();
			} else {
				this.deleteEmptyRooms();
				this.emitLocationUpdate(true, false);
				this.$emit("save-location", this.change_description);
			}
		},
	},
	watch: {
		isNewVersionRequired(newVal) {
			this.isDescriptionNeeded = newVal;
		},

		autoCheckApartments: {
			immediate: true,
			handler(v) {
				if (v) this.localShowApartments = true;
			},
		},
		insulationSubtype: {
			deep: true,
			immediate: true,
			handler(newVal,oldVal) {
				if (!newVal || !this.rooms?.length) return;

				const custType = Number(this.location?.customization ?? 0);
				let hasChanges = false;

				// Customization 0: whole location
				if (custType === 0) {
					const newSubtype = newVal[0];
					const oldSubtype = oldVal?.[0];

					// If changing TO DRY (3), set mounting_step to 15
					if (newSubtype === 3) {
						this.rooms.forEach(room => {
							if (room.mounting_step !== 15) {
								room.mounting_step = 15;
								this.calculateDerivedRoomValues(room);
								hasChanges = true;
							}
						});
					}
					// If changing FROM DRY (3) to something else, reset 15 back to 10
					else if (oldSubtype === 3 && newSubtype !== 3) {
						this.rooms.forEach(room => {
							if (room.mounting_step === 15) {
								room.mounting_step = 10;
								this.calculateDerivedRoomValues(room);
								hasChanges = true;
							}
						});
					}
				}
				// Customization 1: per floor
				else if (custType === 1) {
					// Iterate through each floor's insulation type
					for (const [floorKey, newSubtype] of Object.entries(
						newVal
					)) {
						const oldSubtype = oldVal?.[floorKey];

						// If changing TO DRY (3), set mounting_step to 15
						if (newSubtype === 3) {
							this.rooms.forEach(room => {
								if (
									String(room.floor) === String(floorKey) &&
									room.mounting_step !== 15
								) {
									room.mounting_step = 15;
									this.calculateDerivedRoomValues(room);
									hasChanges = true;
								}
							});
						}
						// If changing FROM DRY (3) to something else, reset 15 back to 10
						else if (oldSubtype === 3 && newSubtype !== 3) {
							this.rooms.forEach(room => {
								if (
									String(room.floor) === String(floorKey) &&
									room.mounting_step === 15
								) {
									room.mounting_step = 10;
									this.calculateDerivedRoomValues(room);
									hasChanges = true;
								}
							});
						}
					}
				}
				// Customization 2: per apartment
				else if (custType === 2) {
					// Iterate through each apartment's insulation type
					for (const [aptKey, newSubtype] of Object.entries(newVal)) {
						const oldSubtype = oldVal?.[aptKey];

						// If changing TO DRY (3), set mounting_step to 15
						if (newSubtype === 3) {
							this.rooms.forEach(room => {
								const roomApt = getUnitId(
									room.apartament ?? room.apartment ?? "0"
								);
								if (
									roomApt === String(aptKey) &&
									room.mounting_step !== 15
								) {
									room.mounting_step = 15;
									this.calculateDerivedRoomValues(room);
									hasChanges = true;
								}
							});
						}
						// If changing FROM DRY (3) to something else, reset 15 back to 10
						else if (oldSubtype === 3 && newSubtype !== 3) {
							this.rooms.forEach(room => {
								const roomApt = getUnitId(
									room.apartament ?? room.apartment ?? "0"
								);
								if (
									roomApt === String(aptKey) &&
									room.mounting_step === 15
								) {
									room.mounting_step = 10;
									this.calculateDerivedRoomValues(room);
									hasChanges = true;
								}
							});
						}
					}
				}

				if (hasChanges) {
					this.$emit("update-location", {
						...this.location,
						rooms: this.rooms,
						isLocationChanged: true,
					});
				}
			},
		},
	},
};
</script>

<style scoped>
.manual-red :deep(.v-field__input) {
	color: rgb(251, 140, 0); /* rosu nice */
	font-weight: 600;
}
.manual-red :deep(.v-text-field__suffix) {
	color: rgb(251, 140, 0); /* rosu nice */
	font-weight: 600;
}
/* optional: si underline rosu */
.manual-red :deep(.v-field__outline) {
	--v-field-border-opacity: 1;
	color: rgb(251, 140, 0);
}

/* Allow the Vuetify toolbar in the bottom slot to grow dynamically */
:deep(.custom-toolbar .v-toolbar__content) {
	height: auto !important; /* Remove the fixed 64px height */
	min-height: 64px; /* Keep the default minimum height */
	align-items: flex-start !important;
	flex-wrap: wrap !important;
	overflow: visible !important;
}

.custom-toolbar {
	width: 100%;
	height: auto !important;
	overflow: visible !important;
}
:deep(.custom-checkbox .v-label) {
	color: rgba(0, 0, 0, 0.801);
	font-weight: 400;
	opacity: 1;
	font-size: 14px !important;
}

.text-right * {
	text-align: right;
}

.no-arrows input[type="number"] {
	-moz-appearance: textfield;
	appearance: textfield;
}

.no-arrows input::-webkit-outer-spin-button,
.no-arrows input::-webkit-inner-spin-button {
	-webkit-appearance: none;
}

/* Native room name input (like old IPA) – matches Vuetify underlined compact look */
.room-name-input {
	width: 100%;
	padding: 4px 0 6px;
	font-size: 14px;
	border: none;
	border-bottom: 1px solid rgba(0, 0, 0, 0.38);
	border-radius: 0;
	background: transparent;
	outline: none;
}
.room-name-input:hover {
	border-bottom-color: rgba(0, 0, 0, 0.87);
}
.room-name-input:focus {
	border-bottom-width: 2px;
	border-bottom-color: rgba(0, 0, 0, 0.87);
}
</style>
